/* ---------------- Connection ---------------- */

export const CONNECT = 'TIME_TRACKER_CONNECT';
export const CONNECT_SUCCESS = 'TIME_TRACKER_CONNECT_SUCCESS';
export const CONNECT_FAILED = 'TIME_TRACKER_CONNECT_FAILED';

export const CONNECT_USER = 'TIME_TRACKER_CONNECT_USER';
export const USE_ADMIN_KEY_FOR_SELF = 'TIME_TRACKER_USE_ADMIN_KEY_FOR_SELF';
export const CONNECT_USER_SUCCESS = 'TIME_TRACKER_CONNECT_USER_SUCCESS';
export const CONNECT_USER_FAILED = 'TIME_TRACKER_CONNECT_USER_FAILED';

export const DISCONNECT = 'TIME_TRACKER_DISCONNECT';
export const DISCONNECT_SUCCESS = 'TIME_TRACKER_DISCONNECT_SUCCESS';
export const DISCONNECT_FAILED = 'TIME_TRACKER_DISCONNECT_FAILED';


export const UPDATE_SETTINGS = 'TIME_TRACKER_UPDATE_SETTINGS';
export const UPDATE_SETTINGS_SUCCESS = 'TIME_TRACKER_UPDATE_SETTINGS_SUCCESS';
export const UPDATE_SETTINGS_FAILED = 'TIME_TRACKER_UPDATE_SETTINGS_FAILED';

/* ---------------- Timer ---------------- */

export const START = 'TIME_TRACKER_START';
export const START_SUCCESS = 'TIME_TRACKER_START_SUCCESS';
export const START_FAILED = 'TIME_TRACKER_START_FAILED';
export const START_DEFERRED = 'TIME_TRACKER_START_DEFERRED';

export const STOP = 'TIME_TRACKER_STOP';
export const STOP_SUCCESS = 'TIME_TRACKER_STOP_SUCCESS';
export const STOP_FAILED = 'TIME_TRACKER_STOP_FAILED';

export const UPDATE = 'TIME_TRACKER_UPDATE';
export const UPDATE_SUCCESS = 'TIME_TRACKER_UPDATE_SUCCESS';
export const UPDATE_FAILED = 'TIME_TRACKER_UPDATE_FAILED';

export const DISCARD = 'TIME_TRACKER_DISCARD';
export const DISCARD_SUCCESS = 'TIME_TRACKER_DISCARD_SUCCESS';
export const DISCARD_FAILED = 'TIME_TRACKER_DISCARD_FAILED';

/* ---------------- Lookups ---------------- */

export const LOAD_BILLABLE = 'TIME_TRACKER_LOAD_BILLABLE';
export const LOAD_BILLABLE_STARTED = 'TIME_TRACKER_LOAD_BILLABLE_STARTED';
export const LOAD_BILLABLE_SUCCESS = 'TIME_TRACKER_LOAD_BILLABLE_SUCCESS';
export const LOAD_BILLABLE_FAILED = 'TIME_TRACKER_LOAD_BILLABLE_FAILED';
export const LOAD_LAST_ENTRY = 'TIME_TRACKER_LOAD_LAST_ENTRY';
export const LOAD_LAST_ENTRY_STARTED = 'TIME_TRACKER_LOAD_LAST_ENTRY_STARTED';
export const LOAD_LAST_ENTRY_SUCCESS = 'TIME_TRACKER_LOAD_LAST_ENTRY_SUCCESS';
export const LOAD_LAST_ENTRY_FAILED = 'TIME_TRACKER_LOAD_LAST_ENTRY_FAILED';
export const PREFETCH_LAST_ENTRY = 'TIME_TRACKER_PREFETCH_LAST_ENTRY';
export const LOAD_TAGS = 'TIME_TRACKER_LOAD_TAGS';
export const LOAD_TAGS_STARTED = 'TIME_TRACKER_LOAD_TAGS_STARTED';
export const LOAD_TAGS_SUCCESS = 'TIME_TRACKER_LOAD_TAGS_SUCCESS';
export const LOAD_TAGS_FAILED = 'TIME_TRACKER_LOAD_TAGS_FAILED';

export const LOAD_CUSTOMERS = 'TIME_TRACKER_LOAD_CUSTOMERS';

export const LOAD_CARD = 'TIME_TRACKER_LOAD_CARD';
export const LOAD_CUSTOMERS_STARTED = 'TIME_TRACKER_LOAD_CUSTOMERS_STARTED';
export const LOAD_CUSTOMERS_SUCCESS = 'TIME_TRACKER_LOAD_CUSTOMERS_SUCCESS';
export const LOAD_CUSTOMERS_FAILED = 'TIME_TRACKER_LOAD_CUSTOMERS_FAILED';

/* ---------------- Internal ---------------- */

export const SET_PENDING_START = 'TIME_TRACKER_SET_PENDING_START';
export const CLEAR_PENDING_START = 'TIME_TRACKER_CLEAR_PENDING_START';

export const DISMISS_DONE_OR_TRASH_MOVE = 'TIME_TRACKER_DISMISS_DONE_OR_TRASH_MOVE';
export const SET_DISMISSED_MOVES = 'TIME_TRACKER_SET_DISMISSED_MOVES';

/* ==================== Connection ==================== */

/**
 * Admin connects (or reconnects, when `mode: 'reconnect'` was used to open the
 * dialog) the account. `credentials` is provider-shaped, e.g. `{ host, apiKey }`.
 */
export const connect = (provider, credentials, accountId) => ({
  type: CONNECT,
  provider,
  credentials,
  accountId,
});

/** Erases stored credentials and turns the integration off. */
export const disconnect = (accountId) => ({
  type: DISCONNECT,
  accountId,
});

/**
 * Updates account-wide preferences (defaultCustomerId, startTimerOnInProgress,
 * descriptionRequired, stopTimerOnDoneOrTrash). Pass every key: the server replaces the
 * whole object.
 */
export const updateSettings = (preferences, accountId) => ({
  type: UPDATE_SETTINGS,
  preferences,
  accountId,
});

/** Current user links (or relinks) their own personal API key. */
export const connectUser = (apiKey, accountId) => ({
  type: CONNECT_USER,
  apiKey,
  accountId,
});

/**
 * Links the connecting admin to the account using the very key they just entered, so
 * they can start a timer without being asked for one again. Dispatched from the activate
 * step when they choose "use this admin connection for my time tracking too".
 *
 * Carries no key of its own: the saga still holds the one from `connect`, and putting a
 * credential in an action would place it in redux devtools and every action log.
 */
export const useAdminKeyForSelf = (accountId) => ({
  type: USE_ADMIN_KEY_FOR_SELF,
  accountId,
});

/* ==================== Timer ==================== */

/**
 * Starts a timer on `cardId`. Does not check whether another timer is running -
 * that rule lives entirely in `startSaga`.
 */
export const start = (cardId, boardId, accountId) => ({
  type: START,
  cardId,
  boardId,
  accountId,
});

/**
 * A start that was held while a blocking dialog resolved, now being retried.
 *
 * Carries `resumed: true` so `startSaga` skips its client-side "is this user linked?"
 * pre-check. That check exists to avoid a doomed round trip, and it reads
 * `TimeTrackerAccountUserSettings` from Firestore - which has not propagated yet in the
 * moment after `PUT /user-connect` returns. Re-running it here would re-open the very
 * dialog the user just completed. On a resume the server has already spoken, so let it
 * be the authority: if it disagrees it answers `USER_NOT_CONNECTED`/`USER_TOKEN_INVALID`
 * and we handle that.
 *
 * `stoppedAt` is the end the stop dialog that held it gave the entry before, if any - the
 * new timer starts from it when it lies ahead and has already passed (see `alignStartWithLastEntry`).
 */
export const _startResumed = (cardId, boardId, accountId, stoppedAt) => ({
  type: START,
  cardId,
  boardId,
  accountId,
  stoppedAt,
  resumed: true,
});

/** Finalizes the running entry. An empty `endedAt` means "now". */
export const stop = ({ tags, billable, description, startedAt, endedAt }, accountId) => ({
  type: STOP,
  tags,
  billable,
  description,
  startedAt,
  endedAt,
  accountId,
});

/** Edits the running entry in place, without stopping it. No `endedAt`. */
export const update = ({ tags, billable, description, startedAt }, accountId) => ({
  type: UPDATE,
  tags,
  billable,
  description,
  startedAt,
  accountId,
});

/** Throws the running entry away instead of saving it. */
export const discard = (accountId) => ({
  type: DISCARD,
  accountId,
});

/* ==================== Lookups ==================== */

/**
 * Reads the running entry's resolved billable value and whether this person's own token
 * may change it.
 *
 * Not lazy and never cached, unlike the lookups below: the tracker resolves billable
 * through its own Client -> Project -> Activity inheritance, and whether the caller may
 * override it can change at any time. Dispatch on every open of the Stop Timer dialog.
 */
export const loadBillable = (accountId) => ({
  type: LOAD_BILLABLE,
  accountId,
});

/**
 * Reads this person's own most recently closed entry from the tracker, to offer its end
 * as a start time for the entry being stopped.
 *
 * Read live on every open, like `loadBillable`: it is the caller's history on the
 * tracker, which Kerika neither stores nor can predict. The account has no timer running
 * requirement - the server answers from the tracker regardless.
 */
export const loadLastEntry = (accountId) => ({
  type: LOAD_LAST_ENTRY,
  accountId,
});

/**
 * `loadLastEntry` ahead of the stop dialog, so its hint is already there when it opens.
 * Nobody asked for it, so a key failure stays quiet rather than raising the connect
 * dialog, and it is skipped while another load for the account is in flight.
 */
export const prefetchLastEntry = (accountId) => ({
  type: PREFETCH_LAST_ENTRY,
  accountId,
});

/**
 * Fetches the connected provider's tags. Lazy - dispatch on first open of the field.
 * Pass `force: true` (the tags dialog's refresh button) to bypass the "already
 * SUCCESS" skip and refetch - for when someone just created a tag in Kimai and
 * doesn't want to reload the whole page to see it.
 */
export const loadTags = (accountId, force = false) => ({
  type: LOAD_TAGS,
  accountId,
  force,
});

/**
 * Fetches the connected provider's customers. Lazy - dispatch on first open of the
 * field. Pass `force: true` (the settings dialog's refresh button) to bypass the
 * "already SUCCESS" skip and refetch - for when the admin just created a customer
 * in Kimai and doesn't want to close and reopen the dialog.
 */
export const loadCustomers = (accountId, force = false) => ({
  type: LOAD_CUSTOMERS,
  accountId,
  force,
});

/**
 * Loads the card a running timer is on, by id.
 *
 * Cards otherwise arrive only through a board's own query, so anywhere outside that
 * board - Explore, Home, another board - the header pill and the Stop Timer dialog knew
 * the card id but had no title to show for it.
 *
 * @param {String} cardId card the running timer belongs to
 * @param {String} [accountId] defaults to the account in the URL
 */
export const loadCard = (cardId, accountId) => ({
  type: LOAD_CARD,
  cardId,
  accountId,
});

/* ==================== Internal ==================== */

/** Drops a held start when its blocking dialog is cancelled. */
export const clearPendingStart = () => ({
  type: CLEAR_PENDING_START,
});

/**
 * Remembers on this device that the prompt for the card's latest move into Done or Trash
 * was closed without stopping, so it does not open again for that move.
 *
 * @param {String} cardId the timed card
 * @param {String} [accountId] defaults to the account in the URL
 */
export const dismissDoneOrTrashMove = (cardId, accountId) => ({
  type: DISMISS_DONE_OR_TRASH_MOVE,
  cardId,
  accountId,
});

/* ==================== Private (dispatched by saga.js only) ==================== */

export const _connectSuccess = (accountId) => ({ type: CONNECT_SUCCESS, accountId });
export const _connectFailed = (accountId, error) => ({ type: CONNECT_FAILED, accountId, error });

export const _connectUserSuccess = (accountId) => ({ type: CONNECT_USER_SUCCESS, accountId });
export const _connectUserFailed = (accountId, error) => ({ type: CONNECT_USER_FAILED, accountId, error });

export const _disconnectSuccess = (accountId) => ({ type: DISCONNECT_SUCCESS, accountId });
export const _disconnectFailed = (accountId, error) => ({ type: DISCONNECT_FAILED, accountId, error });


export const _updateSettingsSuccess = (accountId) => ({ type: UPDATE_SETTINGS_SUCCESS, accountId });
export const _updateSettingsFailed = (accountId, error) => ({ type: UPDATE_SETTINGS_FAILED, accountId, error });

export const _startSuccess = (accountId) => ({ type: START_SUCCESS, accountId });
export const _startFailed = (accountId, error) => ({ type: START_FAILED, accountId, error });

/**
 * The start was not attempted: something has to be resolved first (another timer is
 * running, or this user has no working key), so a dialog was opened instead.
 *
 * Terminal for `requests.start`, which the reducer marks IN_PROGRESS the moment `START`
 * is dispatched. Without this, a deferred start would sit in-flight forever and views
 * keyed off that status - the card detail's "Starting..." chip - would never clear.
 * Not a failure: nothing went wrong, and no error should surface.
 */
export const _startDeferred = (accountId) => ({ type: START_DEFERRED, accountId });

export const _stopSuccess = (accountId) => ({ type: STOP_SUCCESS, accountId });
export const _stopFailed = (accountId, error) => ({ type: STOP_FAILED, accountId, error });

export const _updateSuccess = (accountId) => ({ type: UPDATE_SUCCESS, accountId });
export const _updateFailed = (accountId, error) => ({ type: UPDATE_FAILED, accountId, error });

export const _discardSuccess = (accountId) => ({ type: DISCARD_SUCCESS, accountId });
export const _discardFailed = (accountId, error) => ({ type: DISCARD_FAILED, accountId, error });

/** Marks the fetch in flight. Dispatched by the saga once its skip-check has passed, never by a view. */
export const _loadBillableStarted = (accountId) => ({ type: LOAD_BILLABLE_STARTED, accountId });
export const _loadBillableSuccess = (accountId, status) => ({ type: LOAD_BILLABLE_SUCCESS, accountId, status });
export const _loadBillableFailed = (accountId, error) => ({ type: LOAD_BILLABLE_FAILED, accountId, error });
export const _loadLastEntryStarted = (accountId) => ({ type: LOAD_LAST_ENTRY_STARTED, accountId });
export const _loadLastEntrySuccess = (accountId, entry) => ({ type: LOAD_LAST_ENTRY_SUCCESS, accountId, entry });
export const _loadLastEntryFailed = (accountId, error) => ({ type: LOAD_LAST_ENTRY_FAILED, accountId, error });
export const _loadTagsStarted = (accountId) => ({ type: LOAD_TAGS_STARTED, accountId });
export const _loadTagsSuccess = (accountId, list) => ({ type: LOAD_TAGS_SUCCESS, accountId, list });
export const _loadTagsFailed = (accountId, error) => ({ type: LOAD_TAGS_FAILED, accountId, error });

/** Marks the fetch in flight. Dispatched by the saga once its skip-check has passed, never by a view. */
export const _loadCustomersStarted = (accountId) => ({ type: LOAD_CUSTOMERS_STARTED, accountId });
export const _loadCustomersSuccess = (accountId, list) => ({ type: LOAD_CUSTOMERS_SUCCESS, accountId, list });
export const _loadCustomersFailed = (accountId, error) => ({ type: LOAD_CUSTOMERS_FAILED, accountId, error });

export const _setPendingStart = (cardId, boardId) => ({ type: SET_PENDING_START, cardId, boardId });
export const _setDismissedMoves = (dismissedMoves) => ({ type: SET_DISMISSED_MOVES, dismissedMoves });
